import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LayoutGrid,
  Inbox,
  ChefHat,
  Flame,
  Truck,
  PackageCheck,
} from "lucide-react";
import {
  type PainelData,
  type PainelFilaItem,
  painelService,
} from "../../../services/painelService";
import {
  prioridadeMontagemKey,
  type PrioridadeMontagemAlvo,
} from "../../../services/filaPizzasService";
import { PainelColuna } from "./PainelColuna";
import { CardFilaMontagem } from "./CardFilaMontagem";
import { CardForno } from "./CardForno";
import { CardPedidoEntrega } from "./CardPedidoEntrega";
import { PedidosFinalizadosModal } from "./PedidosFinalizadosModal";
import PedidoOriginalModal from "../../../components/PedidoOriginalModal";
import { itemMatchesDisplayBusca } from "../utils/pedidoSearch";
import {
  isPedidoSalaoFoodfly,
  matchesOrigemFiltro,
  resolveCanalOrigem,
  resolveSalaoProducaoLabels,
  PAOZINHO_CARD_SURFACE_CLASS,
  type PainelOrigemFiltro,
} from "../utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";
import { agruparItensEntregaPorPedido } from "../utils/agruparPedidosPainel";

function resolvePrioridadeAlvo(
  item: PainelFilaItem,
): PrioridadeMontagemAlvo | null {
  if (item.origem === "salao") {
    return item.pedidoFoodflyId != null
      ? { pedidoFoodflyId: item.pedidoFoodflyId }
      : null;
  }
  return item.pedidoIntegracaoId != null
    ? { pedidoIntegracaoId: item.pedidoIntegracaoId }
    : null;
}

interface PainelKanbanProps {
  data: PainelData | null;
  pedidoBusca: string;
  origemFiltro?: PainelOrigemFiltro;
  prioridadesMontagem: string[];
  priorizandoPrioridadeKey: string | null;
  removendoPrioridadeKey: string | null;
  showPriorizarAction: boolean;
  onPriorizarMontagem: (alvo: PrioridadeMontagemAlvo) => Promise<void>;
  onRemoverPrioridadeMontagem: (alvo: PrioridadeMontagemAlvo) => Promise<void>;
  onRefetchPainel: () => Promise<void>;
  lojaId?: number;
  finalizadosModalOpen: boolean;
  onCloseFinalizadosModal: () => void;
}

const PainelKanbanInner: React.FC<PainelKanbanProps> = ({
  data,
  pedidoBusca,
  origemFiltro = "todos",
  prioridadesMontagem,
  priorizandoPrioridadeKey,
  removendoPrioridadeKey,
  showPriorizarAction,
  onPriorizarMontagem,
  onRemoverPrioridadeMontagem,
  onRefetchPainel,
  lojaId,
  finalizadosModalOpen,
  onCloseFinalizadosModal,
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const [pedidoOriginalOrderId, setPedidoOriginalOrderId] = useState<
    number | null
  >(null);

  const openPedidoOriginal = useCallback((orderId: number) => {
    setPedidoOriginalOrderId(orderId);
  }, []);

  const {
    aguardandoMontagem,
    emMontagem,
    emForno,
    aguardandoEntrega,
    emEntrega,
    paozinhosAguardandoBordeiro,
  } = useMemo(() => {
    if (!data) {
      return {
        aguardandoMontagem: [] as PainelData["aguardandoMontagem"],
        emMontagem: [] as PainelData["emMontagem"],
        emForno: [] as PainelData["emForno"],
        aguardandoEntrega: [] as PainelData["aguardandoEntrega"],
        emEntrega: [] as PainelData["emEntrega"],
        paozinhosAguardandoBordeiro:
          [] as PainelData["paozinhosAguardandoBordeiro"],
      };
    }
    const q = pedidoBusca;
    const m = (displayId: number) => itemMatchesDisplayBusca(q, displayId);
    const byOrigem = (canal: "cardapio" | "salao") =>
      matchesOrigemFiltro(origemFiltro, canal);
    return {
      aguardandoMontagem: data.aguardandoMontagem.filter(
        (i) => m(i.displayId) && byOrigem(resolveCanalOrigem(i.origem)),
      ),
      emMontagem: data.emMontagem.filter(
        (i) => m(i.displayId) && byOrigem(resolveCanalOrigem(i.origem)),
      ),
      emForno: data.emForno.filter(
        (i) => m(i.displayId) && byOrigem(resolveCanalOrigem(i.origem)),
      ),
      aguardandoEntrega: data.aguardandoEntrega.filter(
        (i) => m(i.displayId) && byOrigem("cardapio"),
      ),
      emEntrega: data.emEntrega.filter(
        (i) => m(i.displayId) && byOrigem("cardapio"),
      ),
      paozinhosAguardandoBordeiro: (
        data.paozinhosAguardandoBordeiro ?? []
      ).filter(
        (i) =>
          m(i.displayId) &&
          byOrigem(resolveCanalOrigem(i.origem)),
      ),
    };
  }, [data, pedidoBusca, origemFiltro]);

  const prioridadesSet = useMemo(
    () => new Set(prioridadesMontagem),
    [prioridadesMontagem]
  );

  const pedidosAguardandoEntrega = useMemo(
    () => agruparItensEntregaPorPedido(aguardandoEntrega),
    [aguardandoEntrega]
  );
  const pedidosEmEntrega = useMemo(
    () => agruparItensEntregaPorPedido(emEntrega),
    [emEntrega]
  );
  const mediaLabels = useMemo(() => {
    if (!data) return null;
    const { temposMedios } = data;
    return {
      mediaAguardandoMontagem:
        temposMedios.aguardandoMontagemSegundos != null
          ? painelService.formatTempoMedio(
              temposMedios.aguardandoMontagemSegundos
            )
          : null,
      mediaEmMontagem:
        temposMedios.emMontagemSegundos != null
          ? painelService.formatTempoMedio(temposMedios.emMontagemSegundos)
          : null,
      mediaEmForno:
        temposMedios.emFornoSegundos != null
          ? painelService.formatTempoMedio(temposMedios.emFornoSegundos)
          : null,
      mediaAguardandoEntrega:
        temposMedios.aguardandoEntregaSegundos != null
          ? painelService.formatTempoMedio(
              temposMedios.aguardandoEntregaSegundos
            )
          : null,
      mediaEmEntrega:
        temposMedios.emEntregaSegundosPorEndereco != null
          ? painelService.formatTempoMedio(
              temposMedios.emEntregaSegundosPorEndereco
            )
          : null,
    };
  }, [data]);

  if (!data || !mediaLabels) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-neutral-500">
        <LayoutGrid size={48} className="mb-3 opacity-30" />
        <p>Nenhum dado carregado</p>
      </div>
    );
  }

  const {
    mediaAguardandoMontagem,
    mediaEmMontagem,
    mediaEmForno,
    mediaAguardandoEntrega,
    mediaEmEntrega,
  } = mediaLabels;

  return (
    <>
      <div className="flex h-full w-full gap-4 overflow-x-auto overflow-y-hidden p-4">
        <PainelColuna
          title="Aguardando montagem"
          count={aguardandoMontagem.length + paozinhosAguardandoBordeiro.length}
          icon={<Inbox size={20} strokeWidth={2} />}
          mediaText={mediaAguardandoMontagem}
        >
          {paozinhosAguardandoBordeiro.map((item) => {
            const isSalao = isPedidoSalaoFoodfly({ origem: item.origem });
            const salaoLabels = isSalao
              ? resolveSalaoProducaoLabels({
                  mesaId: item.salaoMesaId,
                  comandaId: item.salaoComandaId,
                  identificador: item.salaoIdentificador,
                  displayId: item.displayId,
                })
              : null;
            return (
            <button
              type="button"
              key={`paozinho-${item.id}`}
              onClick={() => {
                if (!isSalao) openPedidoOriginal(item.orderId);
              }}
              data-paozinho-card=""
              className={`flex w-full flex-col rounded-lg p-3 text-left shadow-sm transition hover:brightness-110 ${PAOZINHO_CARD_SURFACE_CLASS}`}
            >
              <SalaoProducaoOrderHeader
                labels={salaoLabels}
                fallbackBadge={
                  isSalao ? undefined : `Pedido #${item.displayId}`
                }
                showSalaoTag={isSalao}
              />
              <span className="mt-1 line-clamp-2 text-sm font-bold text-white">
                Pãozinho — {item.saborNome}
              </span>
              <span className="mt-2 text-[11px] text-white/75">
                Aguardando bordeiro
              </span>
            </button>
            );
          })}
          {aguardandoMontagem.map((item) => {
            const alvo = resolvePrioridadeAlvo(item);
            const key = alvo ? prioridadeMontagemKey(alvo) : null;
            return (
              <CardFilaMontagem
                key={`${item.tipo}-${item.id}`}
                item={item}
                nowMs={nowMs}
                isPrioritario={key != null && prioridadesSet.has(key)}
                showPriorizarButton={showPriorizarAction && alvo != null}
                isPriorizando={key != null && priorizandoPrioridadeKey === key}
                isRemovendo={key != null && removendoPrioridadeKey === key}
                onPriorizar={
                  alvo ? () => onPriorizarMontagem(alvo) : undefined
                }
                onRemover={
                  alvo ? () => onRemoverPrioridadeMontagem(alvo) : undefined
                }
                onVerPedidoOriginal={openPedidoOriginal}
                onRefetchPainel={onRefetchPainel}
              />
            );
          })}
        </PainelColuna>

        <PainelColuna
          title="Em montagem"
          count={emMontagem.length}
          icon={<ChefHat size={20} strokeWidth={2} />}
          mediaText={mediaEmMontagem}
        >
          {emMontagem.map((item) => {
            const alvo = resolvePrioridadeAlvo(item);
            return (
              <CardFilaMontagem
                key={`${item.tipo}-${item.id}`}
                item={item}
                nowMs={nowMs}
                isPrioritario={
                  alvo != null && prioridadesSet.has(prioridadeMontagemKey(alvo))
                }
                showPriorizarButton={false}
                isPriorizando={false}
                onVerPedidoOriginal={openPedidoOriginal}
                onRefetchPainel={onRefetchPainel}
              />
            );
          })}
        </PainelColuna>

        <PainelColuna
          title="Em forno"
          count={emForno.length}
          icon={<Flame size={20} strokeWidth={2} />}
          mediaText={mediaEmForno}
        >
          {emForno.map((item) => (
            <CardForno
              key={item.id}
              item={item}
              nowMs={nowMs}
              onVerPedidoOriginal={openPedidoOriginal}
            />
          ))}
        </PainelColuna>

        <PainelColuna
          title="Aguardando entrega"
          count={pedidosAguardandoEntrega.length}
          icon={<Truck size={20} strokeWidth={2} />}
          mediaText={mediaAguardandoEntrega}
        >
          {pedidosAguardandoEntrega.map((grupo) => (
            <CardPedidoEntrega
              key={grupo[0].displayId}
              itens={grupo}
              fase="aguardando_entrega"
              nowMs={nowMs}
              onVerPedidoOriginal={openPedidoOriginal}
            />
          ))}
        </PainelColuna>

        <PainelColuna
          title="Em entrega"
          count={pedidosEmEntrega.length}
          icon={<PackageCheck size={20} strokeWidth={2} />}
          mediaText={mediaEmEntrega}
        >
          {pedidosEmEntrega.map((grupo) => (
            <CardPedidoEntrega
              key={grupo[0].displayId}
              itens={grupo}
              fase="em_entrega"
              nowMs={nowMs}
              onVerPedidoOriginal={openPedidoOriginal}
            />
          ))}
        </PainelColuna>
      </div>
      {pedidoOriginalOrderId != null && (
        <PedidoOriginalModal
          orderId={pedidoOriginalOrderId}
          lojaId={lojaId}
          isOpen
          overlayZIndexClass="z-[10000]"
          onClose={() => setPedidoOriginalOrderId(null)}
        />
      )}
      <PedidosFinalizadosModal
        isOpen={finalizadosModalOpen}
        onClose={onCloseFinalizadosModal}
        lojaId={lojaId}
        origemFiltro={origemFiltro}
        onVerPedidoOriginal={openPedidoOriginal}
      />
    </>
  );
};

export const PainelKanban = React.memo(PainelKanbanInner);
